import { useState } from "react";
import { ChevronDown, CheckCircle2, AlertTriangle, GraduationCap, ExternalLink, Share2, Trophy } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { partyColors } from "@/lib/party";
import { translateEducation } from "@/lib/i18n";
import PartyChip from "@/components/PartyChip";

export default function CandidateCard({ c, idx, winner = false }) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { t, lang } = useLang();

  const colors = partyColors(c.party_short);
  const name = lang === "ta" && c.name_ta ? c.name_ta : c.name;
  const ageLabel = `${t("age_label")} ${c.age ?? "—"}`;

  const edu = translateEducation(c.education, lang);
  const partyLabel = lang === "ta" ? (c.party_ta || c.party_short) : c.party_short;

  const alliance = lang === "ta" ? c.alliance_ta : c.alliance_en;

  const cases = c.criminal_cases || 0;

  async function handleShare(e) {
    e.stopPropagation();
    const partyLbl = lang === "ta" ? (c.party_ta || c.party_short) : c.party_short;
    const constituencyLbl =
      lang === "ta" ? (c.constituency_ta || c.constituency) : c.constituency;
    const msg = t("share_message")
      .replace("{name}", c.name)
      .replace("{party}", partyLbl)
      .replace("{constituency}", constituencyLbl);
    const url = c.myneta_url || window.location.href;
    const payload = { title: "Know Your Candidates — TN 2026", text: msg, url };

    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch {
      // user cancelled — no-op
    }
    // Fallback: WhatsApp deep link
    const wa = `https://wa.me/?text=${encodeURIComponent(msg + " " + url)}`;
    window.open(wa, "_blank", "noopener");
    try {
      await navigator.clipboard.writeText(msg + " " + url);
      setToast(t("share_copied"));
      setTimeout(() => setToast(""), 1800);
    } catch {
      // ignore
    }
  }

  return (
    <div
      className={`candidate-card bg-white rounded-[22px] p-5 sm:p-6 card-in relative ${winner ? "outline outline-[3px] outline-[#f2b705] bg-[#fffbeb]" : ""}`}
      style={{
        borderLeft: `6px solid ${colors.accent}`,
        boxShadow: "0 1px 2px rgba(17,13,60,.06), 0 10px 30px -18px rgba(17,13,60,.18)",
        animationDelay: `${Math.min(idx * 70, 480)}ms`,
      }}
      data-testid={`candidate-card-${idx}`}
    >
      <button
        className="w-full flex items-start justify-between gap-4 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`candidate-toggle-${idx}`}
      >
        <div className="min-w-0">
          {winner && (
            <span className={`mb-2 inline-flex items-center gap-1.5 rounded-full bg-[#f2b705] px-3 py-1 text-[12px] font-bold text-[#1a1640] ${lang === "ta" ? "font-tamil" : ""}`} data-testid="winner-badge">
              <Trophy size={13} /> {t("winner_2026")}
            </span>
          )}
          <h3 className={`font-display text-2xl sm:text-[28px] font-semibold leading-tight text-[#1a1640] truncate ${lang === "ta" ? "font-tamil" : ""}`}>
            {name}
          </h3>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <PartyChip short={c.party_short} label={partyLabel} />
            <span className="text-[#6b6894] text-sm font-medium">{ageLabel}</span>
          </div>

          <div className="mt-4 flex flex-wrap items-start gap-x-6 gap-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold mb-1">
                {t("criminal_record")}
              </div>
              {cases === 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-emerald-100 text-emerald-700">
                  <CheckCircle2 size={14} /> {t("no_cases")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-rose-100 text-rose-700">
                  <AlertTriangle size={14} /> {cases} {t("cases")}
                </span>
              )}
            </div>

            {edu && edu !== "—" && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#6b6894] font-semibold mb-1">
                  {t("education")}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-medium bg-slate-100 text-slate-700">
                  <GraduationCap size={14} /> {edu}
                </span>
              </div>
            )}
          </div>
        </div>

        <ChevronDown
          size={22}
          strokeWidth={2.2}
          className={`chev text-[#6b6894] shrink-0 mt-1 ${open ? "open" : ""}`}
        />
      </button>

      {open && (
        <div className="accordion-body mt-5" data-testid={`candidate-details-${idx}`}>
          <div className="h-px bg-[#e9e5f5] mb-5" />

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <Metric label={t("total_assets")} value={c.total_assets || "—"} />
            <Metric label={t("liabilities")} value={c.liabilities || "—"} />
          </div>

          {alliance && (
            <Metric className="mt-3" label={t("alliance")} value={alliance} />
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {c.myneta_url ? (
              <a
                href={c.myneta_url}
                target="_blank"
                rel="noreferrer"
                className="myneta-btn"
                data-testid={`myneta-btn-${idx}`}
              >
                {t("view_more")}
                <ExternalLink size={13} strokeWidth={2.4} />
              </a>
            ) : (
              <div
                className="myneta-btn opacity-60 cursor-not-allowed"
                data-testid={`myneta-btn-disabled-${idx}`}
                aria-disabled
                title={t("not_available")}
              >
                {t("view_more")}
              </div>
            )}

            <button
              type="button"
              onClick={handleShare}
              className="share-btn"
              data-testid={`share-btn-${idx}`}
              aria-label={t("share_candidate")}
              title={t("share_candidate")}
            >
              <Share2 size={13} strokeWidth={2.4} />
              {t("share_candidate")}
            </button>
          </div>

          {toast && (
            <div
              className="mt-2 text-[12px] text-emerald-700 font-semibold"
              data-testid={`share-toast-${idx}`}
            >
              {toast}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, className = "" }) {
  return (
    <div
      className={`bg-[#f3eedc] rounded-2xl px-4 py-3 ${className}`}
    >
      <div className="text-[11px] sm:text-xs uppercase tracking-widest text-[#6b6894] font-semibold">
        {label}
      </div>
      <div className="mt-1.5 text-[18px] sm:text-[20px] font-display font-semibold text-[#1a1640] break-words">
        {value}
      </div>
    </div>
  );
}
